import { getModel } from '../../model/registry'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import useApi from '../../hooks/useApi'
import useTranslation from '../../hooks/useTranslation'
import { useStore } from '../pageView/Page'
import { useModalStack } from '../../context/modalStackContext'
import ModalCard from '../modal/ModalCard'
import runFieldsLogic from '../../../../common/logic/runFieldsLogic'
import runLogic from '../../../../common/logic/runLogic'
import { buildApply } from './applyExtraction'
import { newSupplierDefaults, fillFromRegistry, withCountryName } from './newSupplierDefaults'
import useProfile from '../../hooks/useProfile'

import { Form } from '../form/FormComponents'
import { Button, ButtonLine, Separator, Title } from '../generic/Core'
import Collapse from '../generic/Collapse'
import CardOptions from '../select/CardOptions'
import DisplayColumns from '../display/DisplayColumns'
import Alert from '../toast/Alert'
import { collapseCard } from '../../events/collapseCard'

/*
 * "Suggested from document" (task 38.03 §7): what was read off an uploaded
 * receipt or invoice, shown beside it for the user to apply.
 *
 * Nothing reaches the form until Apply; Apply takes what the document says
 * and replaces the form's values and lines with it (applyExtraction.js). The data arrives in the background — OCR, then
 * Gemini's extraction, then Jev's classification — and is refetched when the
 * `file.extract` / `file.classify` broadcasts name this file (the query key
 * carries the file id), with a slow poll as a fallback while it is pending.
 *
 * It is a card section like the form's own: a collapsible Form.Section whose
 * collapsed face is a display (DisplayColumns over the extracted document),
 * with the choices as CardOptions (the app's card picker). Its layout is
 * `panel` in extractionData.
 * A record whose attachment was already applied (`appliedMark`) does not
 * show it again.
 *
 * Props: fileId — the attachment's file id; kind — the `extract` value from
 * the navigator config ('expense'), which picks the mapping in extractionData.
 */

const POLL_MS = 4000
const POLL_FOR_MS = 3 * 60 * 1000

const Muted = styled.span`
  margin-left: 0.5rem;
  color: ${({ theme }) => theme.font.label};
  font-size: var(--font-XS);
  font-weight: 400;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.75rem;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  & > label {
    margin-bottom: 0;
    color: ${({ theme }) => theme.font.label};
    font-size: var(--font-XS);
  }
`

// "Choose one" beside a required picker's label, until it has a choice.
const Needed = styled.span`
  margin-left: 0.5rem;
  padding: 0 0.5rem;
  border-radius: 999px;
  font-size: var(--font-XXS);
  font-weight: 600;
  background: ${({ theme }) => theme.emotive.warning.bg};
  color: ${({ theme }) => theme.emotive.warning.color};
  border: 1px solid ${({ theme }) => theme.emotive.warning.border};
`

const Line = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Notes = styled(Alert)`
  --alert-padding-y: 0.5rem;
  --alert-margin-bottom: 0;

  & > p + p { margin-top: 0.25rem; }
`

const pct = (p) => (typeof p === 'number' ? `${Math.round(p * 100)} %` : null)

const ExtractionPanel = ({ fileId, kind = 'expense' }) => {
    const config = getModel('extractionData')[kind]
    const panel = config.panel
    const [state, setState] = useStore(s => s)
    const { getMe, postMe } = useApi()
    const { t, m } = useTranslation()
    const queryClient = useQueryClient()
    const { pushModal } = useModalStack()
    const { pState } = useProfile()

    // Applied and saved before: the record as it was opened says so. Read
    // once, so applying now keeps the panel until the record is reopened.
    const mark = config.appliedMark
    const appliedBefore = useRef(
        !!(state?.[mark.array] || []).find(a => a[mark.key] === fileId)?.[mark.field]
    ).current

    const [expanded, setExpanded] = useState(panel.onMount !== 'collapse')

    const startedRef = useRef(Date.now())
    // The reading is in the org's language — the server decides (38.03 §3).
    const queryKey = [fileId, 'documentSuggestion']
    const { data, refetch } = useQuery({
        queryKey,
        queryFn: async () => {
            const params = new URLSearchParams(state?.id ? { expenseId: state.id } : {})
            const [extract, classify] = await Promise.all([
                getMe(`file/${fileId}/extract?${params}`),
                getMe(`file/${fileId}/classify?${params}`)
            ])
            // A failed GET comes back as the axios error response, whose
            // `status` is a number; a view's status is a word.
            const ok = (v) => (typeof v?.status === 'string' ? v : null)
            return { extract: ok(extract), classify: ok(classify) }
        },
        enabled: !!fileId && !appliedBefore,
        // SSE is the signal; this is the fallback while the file is still being read.
        refetchInterval: (query) => {
            const d = query.state.data
            const waiting = !d?.extract || d.extract.status === 'pending'
                || (d.extract.status === 'done' && (!d.classify || ['pending', 'none'].includes(d.classify.status)))
            return waiting && Date.now() - startedRef.current < POLL_FOR_MS ? POLL_MS : false
        }
    })

    const extract = data?.extract
    const classify = data?.classify?.status === 'done' ? data.classify : null
    const doc = extract?.status === 'done' ? extract.result : null

    // ─── the user's picks ───────────────────────────────────────────────────
    const [supplierId, setSupplierId] = useState(null)
    const opening = useRef(false)   // the supplier card is on its way (createSupplier)
    const [titles, setTitles] = useState([])
    const [accounts, setAccounts] = useState([])
    const [vats, setVats] = useState([])
    const [applying, setApplying] = useState(false)
    const [applied, setApplied] = useState(null)
    const [error, setError] = useState(null)

    // Defaults from the classification: only pre-selected answers are picked.
    useEffect(() => {
        if (!doc) return
        setTitles(doc.lines.map(() => 0))
        const lines = classify?.lines ?? []
        setAccounts(doc.lines.map((_, i) => {
            const c = lines[i]
            return c?.preselect && c.expenseAccountId ? { id: c.expenseAccountId, name: c.name, number: c.number } : null
        }))
        // No pre-selected VAT code: Apply falls back to the org's rule.
        setVats(doc.lines.map((_, i) => {
            const v = lines[i]?.vat
            return v?.preselect && v.taxId ? v.taxId : null
        }))
        setSupplierId(classify?.supplier?.preselect ? classify.supplier.contactId : null)
    }, [doc, classify])

    const accountOptions = useMemo(() => (classify?.lines ?? []).map(c => {
        const list = [...(c?.alternatives || [])]
        if (c?.expenseAccountId && !list.some(a => a.id === c.expenseAccountId)) {
            list.unshift({ id: c.expenseAccountId, name: c.name, number: c.number, probability: c.probability })
        }
        return list
    }), [classify])

    // A VAT choice only where there was one to make (Jev's, between codes at one rate).
    const vatOptions = useMemo(() => (classify?.lines ?? []).map(c => {
        const v = c?.vat
        if (!v || v.method !== 'jev') return []
        const list = [...(v.alternatives || [])]
        if (v.taxId && !list.some(a => a.id === v.taxId)) list.unshift({ id: v.taxId, name: v.name, probability: v.probability })
        return list
    }), [classify])

    const supplierOptions = useMemo(() => {
        const s = classify?.supplier
        const list = [...(s?.alternatives || [])]
        if (s?.contactId && !list.some(a => a.id === s.contactId)) list.unshift({ id: s.contactId, name: s.name, probability: s.probability })
        return list
    }, [classify])

    if (appliedBefore || !extract) return null

    // ─── states without a suggestion ────────────────────────────────────────
    const requestRead = async () => {
        setError(null)
        const res = await postMe(`file/${fileId}/extract`, { expenseId: state?.id ?? null })
        if (res?.error) setError(t('document.extract.error.request'))
        startedRef.current = Date.now()
        refetch()
    }

    const heading = (status) => (
        <Title>{t(panel.title)}{status && <Muted>{status}</Muted>}</Title>
    )

    if (extract.status === 'pending' || (doc && data?.classify?.status === 'pending')) {
        return <Form.Section data-testid="documentSuggestion">{heading(t('document.extract.reading'))}</Form.Section>
    }
    if (!doc) {
        const reason = extract.error ? t(`document.extract.error.${extract.error}`) : null
        return (
            <Form.Section data-testid="documentSuggestion">
                {heading(reason)}
                {error && <Notes className="error"><p>{error}</p></Notes>}
                {extract.status !== 'unsupported' && extract.error !== 'ocr_unsupported' && (
                    <ButtonLine>
                        <Button type="button" $variant="secondary" onClick={requestRead}>{t('document.extract.read')}</Button>
                    </ButtonLine>
                )}
            </Form.Section>
        )
    }

    // ─── apply ──────────────────────────────────────────────────────────────
    const apply = async () => {
        setApplying(true)
        setError(null)
        try {
            const selection = { supplierId, titles, accounts, vat: vats }
            const { changes, fieldsLogic, report } = await buildApply({
                state, extraction: doc, selection, config,
                context: { orgCountry: pState.currentOrgCountry, orgCurrency: pState.currentOrgCurrency, fileId },
                deps: {
                    getContact: async (id) => (await getMe(`${config.supplier.entity}/${id}`))?.data ?? null,
                    getVatCodes: (params) => getMe(`${config.vatTax.entity}?${new URLSearchParams(params)}`),
                    getCurrencyName: async (code) => {
                        const list = await getMe(`${config.currency.collection}/?country=${pState.currentOrgCountry}`)
                        return list?.data?.find(c => c.id === code)?.name ?? null
                    }
                }
            })

            // Through the same path as typing: field logic for what changed,
            // then the whole calculation chain, as Save does.
            const next = { ...state, ...changes }
            const withFields = fieldsLogic.length
                ? await runFieldsLogic(next, state.entity, fieldsLogic, state)
                : next
            const processed = await runLogic(withFields, state.entity, state)
            setState({ ...processed, savedOn: new Date() })
            setApplied(report)
            setExpanded(false)
            // The form reads back as displays: what was applied, at a glance.
            if (panel.collapseFormOnApply) collapseCard(state.entity)

            // The ground truth for tuning: suggested vs what the user applied.
            postMe(`file/${fileId}/extract/applied`, {
                expenseId: state?.id ?? null,
                language: extract.language ?? null,
                report,
                fields: {
                    supplier: { suggested: classify?.supplier?.contactId ?? null, preselected: !!classify?.supplier?.preselect, applied: supplierId },
                    lines: doc.lines.map((_, i) => ({
                        account: { suggested: classify?.lines?.[i]?.expenseAccountId ?? null, preselected: !!classify?.lines?.[i]?.preselect, applied: accounts[i]?.id ?? null },
                        vat: { suggested: classify?.lines?.[i]?.vat?.taxId ?? null, preselected: !!classify?.lines?.[i]?.vat?.preselect, chosen: vats[i] ?? null, applied: changes[config.lines.array]?.[i]?.[config.lines.tax.id] ?? null },
                        title: titles[i] ?? 0
                    }))
                }
            }).catch(() => { })
        } catch {
            setError(t('document.extract.error.apply'))
        } finally {
            setApplying(false)
        }
    }

    const actions = { apply }

    // A supplier the org does not have yet: its contact card, prefilled from
    // the document and, for what it leaves empty, from the company registry.
    const createSupplier = async () => {
        if (opening.current) return
        opening.current = true
        const ns = config.newSupplier
        let defaults
        try {
            const filled = await fillFromRegistry(newSupplierDefaults(ns, doc.supplier, pState.currentOrgCountry), ns, getMe)
            defaults = await withCountryName(filled, async () => (await getMe(`country/?country=${pState.currentOrgCountry}`))?.data)
        } finally {
            opening.current = false
        }
        pushModal(ModalCard, {
            entity: ns.entity,
            isVisible: true,
            setIsVisible: () => { },
            mode: 'fullscreen',
            defaultValues: defaults,
            testId: 'documentSuggestion.createSupplier.modalView',
            opacity: 0.5,
            effect: async (saved) => {
                const id = saved?.data?.id ?? saved?.id
                if (!id) return
                setSupplierId(id)
                // Reading the suggestion again finds the new contact by its tax
                // id or email (checked on every read): no reclassification.
                queryClient.invalidateQueries({ queryKey: [fileId] })
            }
        })
    }

    // ─── render ─────────────────────────────────────────────────────────────
    const warnings = [...(extract.warnings || []), ...(classify?.warnings || [])]
    const warningText = (w) => {
        if (w.code === 'duplicateDocument' || w.code === 'duplicateInvoice') {
            return m(`document.extract.warning.${w.code}`, [(w.values.expenses || []).map(e => e.number).join(', ')])
        }
        return t(`document.extract.warning.${w.code}`)
    }

    // What Apply left open, one sentence each: which line, and why.
    const skipText = (s) => {
        if (s.reason === 'ambiguousVat') {
            return m('document.extract.skipped.vatRate', [s.line, `${Math.round(s.rate * 1000) / 10} %`])
        }
        if (s.reason === 'noRate') return m('document.extract.skipped.noRate', [s.line])
        return t(`document.extract.skipped.${s.reason}`)
    }

    const summary = { ...doc, lineCount: doc.lines.length }
    const exact = ['taxId', 'email'].includes(classify?.supplier?.method)
    const status = applied
        ? t('document.extract.applied')
        : (classify ? null : t('document.extract.classifying'))

    return (
        <Form.Section data-testid="documentSuggestion">
            <Collapse expanded={expanded} toggleExpanded={() => setExpanded(e => !e)} testId="documentSuggestion.toggle" />
            {heading(status)}
            <DisplayColumns config={panel.displaySection} displayData={summary} currency={doc.currency} />

            {!expanded && applied?.skipped.length > 0 && (
                <Notes className="info">{applied.skipped.map(skipText).map((text, i) => <p key={i}>{text}</p>)}</Notes>
            )}

            {expanded && (
                <Body>
                    <Field>
                        <Form.Label>
                            {t(panel.supplier.label)}
                            {panel.supplier.required && !supplierId && <Needed data-testid="documentSuggestion.supplier.needed">{t('document.extract.needed')}</Needed>}
                        </Form.Label>
                        {supplierOptions.length > 0 && (
                            <CardOptions wrap
                                options={supplierOptions.map(s => ({ id: s.id, title: s.name, badge: exact ? null : pct(s.probability) }))}
                                value={supplierId} onChange={setSupplierId} allowClear count={panel.supplier.count} needed={panel.supplier.required} testId="documentSuggestion.supplier" />
                        )}
                        {!classify?.supplier?.preselect && (
                            <ButtonLine style={{ paddingTop: 0, justifyContent: 'start' }}>
                                <Button type="button" $variant="secondary" onClick={createSupplier} data-testid="documentSuggestion.createSupplier">
                                    {m('document.extract.createSupplierNamed', [doc.supplier?.name || '–'])}
                                </Button>
                            </ButtonLine>
                        )}
                    </Field>

                    {doc.lines.map((line, i) => (
                        <Line key={i} data-testid={`documentSuggestion.line.${i + 1}`}>
                            <Separator style={{ marginTop: 0, marginBottom: 0 }} />
                            <DisplayColumns config={panel.lines.displaySection} displayData={line} currency={doc.currency} />
                            {line.suggestions.length > 1 && (
                                <Field>
                                    <Form.Label>{t(panel.lines.title.label)}</Form.Label>
                                    <CardOptions wrap
                                        options={line.suggestions.map((s, k) => ({ id: String(k), title: s.title, subtitle: s.description }))}
                                        value={String(titles[i] ?? 0)} count={panel.lines.title.count}
                                        onChange={id => setTitles(ts => ts.map((v, j) => (j === i ? Number(id) : v)))}
                                        testId={`documentSuggestion.line.${i + 1}.title`} />
                                </Field>
                            )}
                            {(accountOptions[i] || []).length > 0 && (
                                <Field>
                                    <Form.Label>
                                        {t(panel.lines.account.label)}
                                        {panel.lines.account.required && !accounts[i]?.id && <Needed data-testid={`documentSuggestion.line.${i + 1}.account.needed`}>{t('document.extract.needed')}</Needed>}
                                    </Form.Label>
                                    <CardOptions wrap
                                        options={accountOptions[i].map(a => ({ id: a.id, title: a.name, subtitle: a.number, badge: pct(a.probability) }))}
                                        value={accounts[i]?.id ?? null} allowClear count={panel.lines.account.count} needed={panel.lines.account.required}
                                        onChange={id => {
                                            const a = accountOptions[i].find(x => x.id === id) ?? null
                                            setAccounts(as => as.map((v, j) => (j === i ? a : v)))
                                        }}
                                        testId={`documentSuggestion.line.${i + 1}.account`} />
                                </Field>
                            )}
                            {(vatOptions[i] || []).length > 0 && (
                                <Field>
                                    <Form.Label>{t(panel.lines.vat.label)}</Form.Label>
                                    <CardOptions wrap
                                        options={vatOptions[i].map(v => ({ id: v.id, title: v.name, badge: pct(v.probability) }))}
                                        value={vats[i] ?? null} allowClear count={panel.lines.vat.count}
                                        onChange={id => setVats(vs => vs.map((v, j) => (j === i ? id : v)))}
                                        testId={`documentSuggestion.line.${i + 1}.vat`} />
                                </Field>
                            )}
                        </Line>
                    ))}

                    {(warnings.length > 0 || error) && (
                        <Notes className={error ? 'error' : 'warning'}>
                            {warnings.map((w, i) => <p key={i}>{warningText(w)}</p>)}
                            {error && <p>{error}</p>}
                        </Notes>
                    )}

                    <ButtonLine style={{ paddingTop: 0 }}>
                        {panel.buttons.map(b => (
                            <Button key={b.id} type="button" $variant={b.variant} $marginRight="0" disabled={applying}
                                onClick={actions[b.action]} data-testid={b.id}>
                                {t(b.label)}
                            </Button>
                        ))}
                    </ButtonLine>
                </Body>
            )}
        </Form.Section>
    )
}

export default ExtractionPanel
